import { zodResolver } from '@hookform/resolvers/zod'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { z } from 'zod'

import { BusinessRoute } from '@/constants/paths'
import { guideUrlByLocale, templateUrlByLocale } from '@/constants/urls'
import { useKeyPress } from '@/hooks/useKeyPress'
import { cn } from '@/lib/utils'
import { useLocale } from '@/providers/LocaleProvider'
import { GoBackButton } from '@/shared/components'
import { CloudDownload } from '@/shared/icons/outline'
import {
  Button,
  buttonVariants,
  Form,
  FormControl,
  FormField,
  FormItem,
  SlideInScreen,
  StickyContainer,
  Typography,
} from '@/shared/ui'

import { BulkContractorsCSVFileField } from '../BulkContractorsCSVFileField'

const BULK_UPLOAD_FORM_ID = 'bulk-upload-form-id'

const csvSchema = z.object({
  csvFiles: z.array(z.instanceof(File)),
})

type CSVSchema = z.infer<typeof csvSchema>

type Props = {
  onContinue: () => void
}

export const BulkUploadContractorsUpload = ({ onContinue }: Props) => {
  const { locale } = useLocale()
  const form = useForm<CSVSchema>({
    mode: 'onChange',
    resolver: zodResolver(csvSchema),
    defaultValues: {
      csvFiles: [],
    },
  })

  const onSubmit: SubmitHandler<Record<string, File[]>> = async () => {
    onContinue()
  }

  useKeyPress('Enter', onContinue)

  const hasAnyDocuments = form.watch('csvFiles').length > 0

  return (
    <>
      <GoBackButton to={BusinessRoute.ContractorsOverview} />

      <SlideInScreen>
        <Typography text="center" variant="h3">
          <FormattedMessage
            defaultMessage="Add all your contractors at once"
            id="contractors.bulkUpload.title"
          />
        </Typography>
        <div className="p-2" />
        <Typography className="text-center">
          <FormattedMessage
            defaultMessage="Ideal when you're getting started. Upload a CSV file to create multiple contractors at once"
            id="contractors.bulkUpload.subtitle"
          />
        </Typography>
        <div className="p-6" />

        <div className="flex flex-wrap items-center justify-between gap-4 md:flex-nowrap md:gap-0">
          <div className="flex flex-col flex-wrap  gap-2">
            <Typography bold>
              <FormattedMessage
                defaultMessage="Download the contractors CSV template"
                id="bulkPayment.uploadFile.downloadTemplate"
              />
            </Typography>
            <Typography>
              <FormattedMessage
                id="bulkPayment.uploadFile.downloadGuide"
                defaultMessage="Our guide helps you fill it in, find it {here}"
                values={{
                  here: (
                    <a
                      href={guideUrlByLocale[locale]}
                      download
                      target="_blank"
                      rel="noreferrer noopener"
                      className={cn(
                        buttonVariants({
                          variant: 'link',
                          size: 'inline',
                        }),
                      )}
                    >
                      <FormattedMessage
                        defaultMessage="here"
                        id="action.lowercase.here"
                      />
                    </a>
                  ),
                }}
              />
            </Typography>
          </div>

          <Button
            size="md"
            leftIcon={<CloudDownload className="size-5" />}
            type="button"
            variant="tertiary"
            asChild
          >
            <a
              href={templateUrlByLocale[locale]}
              download
              target="_blank"
              rel="noreferrer noopener"
            >
              <FormattedMessage
                defaultMessage="Download template"
                id="action.downloadTemplate"
              />
            </a>
          </Button>
        </div>

        <div className="p-4" />
        <Form {...form}>
          <form
            id={BULK_UPLOAD_FORM_ID}
            className="w-full"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormField
              control={form.control}
              name="csvFiles"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <BulkContractorsCSVFileField
                      name={field.name}
                      files={field.value}
                      onDrop={(files) => {
                        form.setValue(field.name, files)
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </form>
        </Form>
        <StickyContainer>
          <Button
            width="full"
            disabled={!hasAnyDocuments}
            form={BULK_UPLOAD_FORM_ID}
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
          >
            <FormattedMessage defaultMessage="Upload" id="action.upload" />
          </Button>
        </StickyContainer>
      </SlideInScreen>
    </>
  )
}
